import React, { useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';

const TYPE_LABELS = {
  sale: 'Sales',
  enrollment: 'Enrollments',
  dividend: 'Dividend payments',
  commission: 'Commissions',
  investor: 'Investors',
  investorToken: 'Investor tokens',
  user: 'Users',
  userGroup: 'Group memberships',
  group: 'Groups',
  groupProductCommission: 'Group commission overrides',
  product: 'Products',
  productDetails: 'Product details',
  commissionRatio: 'Commission ratios',
  threshold: 'Thresholds',
};

const DeleteImpactModal = ({
  open,
  entityType,
  entityId,
  title = 'Delete record',
  onClose,
  onDeleted,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    if (!open || !entityType || entityId == null) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setImpact(null);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(
          `${getApiUrl()}/api/recycle-bin/impact/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
          { headers: getAuthHeaders(), signal: controller.signal }
        );
        const result = await response.json();
        if (!cancelled) {
          if (result.success) setImpact(result.data);
          else setError(result.message || 'Failed to load linked records');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.name === 'AbortError'
            ? 'The server took too long to check linked records. Restart the backend and try again.'
            : 'Unable to connect to server. Make sure the backend is running.');
        }
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, entityType, entityId]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${getApiUrl()}/api/recycle-bin/delete`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ entityType, entityId }),
      });
      const result = await response.json();
      if (result.success) {
        onDeleted?.(result.data);
        onClose?.();
      } else {
        setError(result.message || 'Failed to delete');
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const groupedDeletes = (impact?.willDelete || []).reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="neo-modal p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
            <AlertCircle size={24} />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{title}</h2>
              {impact?.label && (
                <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">{impact.label}</p>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="overflow-y-auto flex-1 space-y-4">
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-dark-muted">Checking linked records...</p>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-dark-muted">
                Linked records will be moved to the recycle bin together. You can restore them later from Recycle Bin.
              </p>

              {impact?.willDelete?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text mb-2">
                    Will be deleted ({impact.willDelete.length})
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(groupedDeletes).map(([type, items]) => (
                      <div key={type} className="neo-inset p-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-dark-muted mb-2">
                          {TYPE_LABELS[type] || type} ({items.length})
                        </p>
                        <ul className="space-y-1 max-h-40 overflow-y-auto">
                          {items.map((item) => (
                            <li key={`${item.type}-${item.id}`} className="text-sm text-gray-800 dark:text-dark-text">
                              {item.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {impact?.willUnlink?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text mb-2">
                    Will be unlinked ({impact.willUnlink.length})
                  </h3>
                  <ul className="neo-inset p-3 space-y-1 max-h-40 overflow-y-auto">
                    {impact.willUnlink.map((item) => (
                      <li key={`${item.type}-${item.id}-${item.label}`} className="text-sm text-gray-800 dark:text-dark-text">
                        {item.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 neo-btn text-gray-700 dark:text-dark-text">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || submitting || !impact}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-2xl hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Deleting...' : 'Delete linked records'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteImpactModal;
